import tooltipIcon from 'assets/icons/tooltip.svg';
import React, { Fragment, useContext } from 'react';
import { Field, FieldRenderProps } from 'react-final-form';
import { Column, Input, LocaleContext, Row, Select, Tooltip } from 'shared';
import { makeCountrySelectOptions } from 'utils/countryList/countryList';
import {
  combineValidators,
  isValidUrl,
  required as requiredValidator,
  validateBusinessRegistrationNumber,
} from 'utils/formValidators';

import { AccountFormTooltip, CompanyDataRow } from '../../AccountForm.styles';

const formatCountry = (value?: any) => (value ? { value, label: value } : undefined);
const parseCountry = (value?: any) => (value ? value.value : '');

export const CompanyDetails = () => {
  const localeContext = useContext(LocaleContext);

  const required = requiredValidator(localeContext.formatMessage({ id: 'FORM.REQUIRED' }));
  const urlValidation = isValidUrl(localeContext.formatMessage({ id: 'FORM.INVALID-URL' }));

  return (
    <Fragment>
      <Field name="companyData.name" validate={required}>
        {({ input, meta }: FieldRenderProps<HTMLInputElement>) => (
          <Input
            label={localeContext.formatMessage({ id: 'ACCOUNT.COMPANY-DETAILS.COMPANY-NAME' })}
            input={input}
            meta={meta}
            className="acount-form__company-name"
            maxLenght={200}
            charsCounter={true}
          />
        )}
      </Field>
      <CompanyDataRow>
        <Field
          name="companyData.nip"
          validate={combineValidators(
            required,
            validateBusinessRegistrationNumber(
              localeContext.formatMessage({ id: 'FORM.INVALID-BUSINESS-REGISTRATION-NUMBER' }),
            ),
          )}
        >
          {({ input, meta }: FieldRenderProps<HTMLInputElement>) => (
            <Input
              label={localeContext.formatMessage({ id: 'ACCOUNT.COMPANY-DETAILS.BUSINESS-REGISTRATION-NUMBER' })}
              input={input}
              meta={meta}
              className="acount-form__company-nip"
              maxLenght={14}
              charsCounter={true}
            />
          )}
        </Field>
        <Tooltip
          content={
            <AccountFormTooltip>
              {localeContext.formatMessage({ id: 'FORM.BUSINESS-REGISTRATION-NUMBER-TIP' })}
            </AccountFormTooltip>
          }
        >
          {({ ref }) => <img ref={ref} src={tooltipIcon} alt="" />}
        </Tooltip>
      </CompanyDataRow>
      <Field name="companyData.country" validate={required} format={formatCountry} parse={parseCountry}>
        {({ input, meta }: { meta: FieldRenderProps<any>['meta']; input: any }) => (
          <Select
            placeholder={localeContext.formatMessage({ id: 'ACCOUNT.COMPANY-DETAILS.COUNTRY' })}
            input={{ ...input, isSearchable: true, onFocus: (e: any) => e.preventDefault() }}
            meta={meta}
            className="acount-form__company-country"
            options={makeCountrySelectOptions()}
            menuPlacement="top"
          />
        )}
      </Field>
      <Field name="companyData.address" validate={required}>
        {({ input, meta }: FieldRenderProps<HTMLInputElement>) => (
          <Input
            label={localeContext.formatMessage({ id: 'ACCOUNT.COMPANY-DETAILS.ADDRESS-LINE-1' })}
            input={input}
            meta={meta}
            className="acount-form__company--address"
            maxLenght={100}
            charsCounter={true}
          />
        )}
      </Field>
      <Field name="companyData.addressDetails">
        {({ input, meta }: FieldRenderProps<HTMLInputElement>) => (
          <Input
            label={localeContext.formatMessage({ id: 'ACCOUNT.COMPANY-DETAILS.ADDRESS-LINE-2' })}
            input={input}
            meta={meta}
            className="acount-form__company-address-details"
            maxLenght={100}
            charsCounter={true}
          />
        )}
      </Field>
      <Row>
        <Column size={[12, 12, 6]}>
          <Field name="companyData.zipCode" validate={required}>
            {({ input, meta }: FieldRenderProps<HTMLInputElement>) => (
              <Input
                label={localeContext.formatMessage({ id: 'ACCOUNT.COMPANY-DETAILS.ZIP' })}
                input={input}
                meta={meta}
                className="acount-form__company-zip"
                maxLenght={10}
                charsCounter={true}
              />
            )}
          </Field>
        </Column>
        <Column size={[12, 12, 6]}>
          <Field name="companyData.city" validate={required}>
            {({ input, meta }: FieldRenderProps<HTMLInputElement>) => (
              <Input
                label={localeContext.formatMessage({ id: 'ACCOUNT.COMPANY-DETAILS.CITY' })}
                input={input}
                meta={meta}
                className="acount-form__company-city"
                maxLenght={50}
                charsCounter={true}
              />
            )}
          </Field>
        </Column>
      </Row>
      <CompanyDataRow>
        <Field name="companyData.website" validate={combineValidators(required, urlValidation)}>
          {({ input, meta }: FieldRenderProps<HTMLInputElement>) => (
            <Input
              label={localeContext.formatMessage({ id: 'ACCOUNT.COMPANY-DETAILS.WEBSITE' })}
              input={input}
              meta={meta}
              className="acount-form__company-website"
              maxLenght={100}
              charsCounter={true}
            />
          )}
        </Field>
        <Tooltip
          content={<AccountFormTooltip>{localeContext.formatMessage({ id: 'FORM.URL-TIP' })}</AccountFormTooltip>}
        >
          {({ ref }) => <img ref={ref} src={tooltipIcon} alt="" />}
        </Tooltip>
      </CompanyDataRow>
    </Fragment>
  );
};
